import { HttpClient } from '@angular/common/http';
import { BiosamplesSortFields } from '@app/biosamples/datasource/biosamples.datasource';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { RadDropdownSelectRendererComponent, RadDropdownSelectRendererParams } from '@app/shared/cell-renderers/rad-dropdown-select-renderer/rad-dropdown-select-renderer.component';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { CustomColDef } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';


export enum BiosampleSearchColumnFields {
    Name = 'BioSampleName',
    LibraryPrepKit = 'LibraryPrepKit',
    DefaultProjectName = 'DefaultProjectName',
    Updated = 'Updated',
    Created = 'Created',
    Owner = 'Owner',
    LabStatus = 'LabStatus',
    Status = 'Status'
}

export class BiosampleSearchGridOptionsFormatter extends SearchGridOptionsFormatter<BiosampleSearchColumnFields> {
    private biosampleResourceConfig: ISearchResourceConfig;
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
      public resourceDictionaryService: SearchResourceDictionaryService, 
      public bsshDatePipe: BsshDatePipe,
      public http: HttpClient
    ) {
        super(resourceDictionaryService, SearchResourceType.Biosample);
        this.biosampleResourceConfig = this.resourceDictionaryService.getResourceConfigByType(SearchResourceType.Biosample);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
      return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
          params: {
              limit: '50',
              scope: 'projects',
              sortBy: 'ModifiedOn',
              sortDir: 'desc',
              query: arg ? `Project.Name:"${arg}"` : ''
          },
          withCredentials: true
      });
  }

  updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
      return this.getProjectFilterOptions(arg).pipe(
        map((response) => {
          return this.toProjectFilterOptions(response);
        })
      );
  }

  bindFilterUpdateCallbacksToCurrentContext() {
      this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
  }

  toProjectFilterOptions(result): FilterOption[] {
      const projects: [] = result.Response.Items;
      const filteroptions: FilterOption[] = [];
      projects.forEach((item: any) => {
        const filterOption: FilterOption = {
          displayName: item.Project.Name,
          filterValue: item.Project.Id
        };
        filteroptions.push(filterOption);

      });
      return filteroptions;
  }

    protected getOverrideColumnParameters(): OverrideColumnParameters<BiosampleSearchColumnFields> {
        const inclusiveColumns: BiosampleSearchColumnFields[] = [
            BiosampleSearchColumnFields.Name,
            BiosampleSearchColumnFields.LibraryPrepKit,
            BiosampleSearchColumnFields.DefaultProjectName,
            BiosampleSearchColumnFields.Owner,
            BiosampleSearchColumnFields.Updated
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<BiosampleSearchColumnFields>[] = [
            {
              colId: BiosampleSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions(): CustomColDef[] {
        const biosampleConfig = this.biosampleResourceConfig;
        return [
            {
                headerName: 'Name',
                field: 'BioSample.BioSampleName',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        return DataTableUtilities.getRadLinkParams(
                            params.data.BioSample.BioSampleName,
                            biosampleConfig.uiHrefAccessor(params.data)
                        );
                    } else {
                        return {};
                    }
                },
                flex: 3.5,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: false,
                sortable: false,
                colId: BiosampleSearchColumnFields.Name,
            },
            {
                headerName: 'Library Kit',
                field: 'BioSample.LibraryPreps',
                cellRendererFramework: RadDropdownSelectRendererComponent,
                valueGetter: (params) => {
                    // Return the selected library prep IDs instead of the full LibraryPreps array
                    if (params.data && params.data.selectedLibraryPrepIds) {
                        return params.data.selectedLibraryPrepIds;
                    }
                    return [];
                },
                cellRendererParams: (params) => {
                    if(params.data && params.data.BioSample && params.data.BioSample.LibraryPreps) {
                        const libraryPrepOptions = params.data.BioSample.LibraryPreps.map((libraryPrepOption) => {
                          return {
                            value: libraryPrepOption.Id,
                            text: libraryPrepOption.Name
                          }
                        })
                        return {
                          options: libraryPrepOptions,
                          multiple: true,
                          // Note: We don't pass value here - it will be set via updateLibraryPrepCellRenderer
                          onSelectionChange: (newValue, cellParams) => {
                            // This callback will be provided via grid context
                            if (params.context && params.context.onLibraryPrepSelectionChange) {
                              params.context.onLibraryPrepSelectionChange(newValue, cellParams);
                            }
                          },
                          selectedOptionIdsPropertyName: 'selectedLibraryPrepIds'
                        } as RadDropdownSelectRendererParams
                    } else {
                        return {
                          options: [],
                          multiple: true
                        };
                    }
                },
                colId: BiosamplesSortFields.LibraryPrepKit,
                sortable: false,
                minWidth: ColumnMinWidth.RESOURCE,
                flex: 3
            },
            {
                headerName: 'Project',
                field: 'BioSample.DefaultProject.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.BioSample && params.data.BioSample.DefaultProject) {
                        const project = params.data.BioSample.DefaultProject;
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            `/projects/${project.Id}`
                        );
                    } else {
                        return {};
                    }
                },
                colId: BiosamplesSortFields.DefaultProjectName,
                sortable: false,
                minWidth: ColumnMinWidth.RESOURCE,
                flex: 3,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback
            },
              {
                headerName: 'Updated',
                valueGetter: (params) => {
                  if (!params.data) { return; }
                  return this.bsshDatePipe.transform(params.data.BioSample.DateModified);
                },
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE,
                filterActive: false,
                colId: BiosampleSearchColumnFields.Updated,
                resizable: true,
                sortable: false,
                cellRenderer: 'span',
              },
              {
                headerName: 'Created',
                valueGetter: (params) => {
                  if (!params.data) { return; }
                  return this.bsshDatePipe.transform(params.data.BioSample.DateCreated);
                },
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE,
                filterActive: false,
                colId: BiosampleSearchColumnFields.Created,
                resizable: true,
                sortable: false,
                cellRenderer: 'span',
              },
              {
                headerName: 'Owner',
                field: 'BioSample.UserOwnedBy.Name',
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE,
                colId: BiosampleSearchColumnFields.Owner,
                sortable: false,
                cellRenderer: 'span',
              },
              {
                headerName: 'Lab Status',
                field: 'BioSample.LabStatus',
                suppressSizeToFit: true,
                cellRenderer: 'span',
                maxWidth: 160,
                minWidth: 80,
                filterActive: false,
                colId: BiosampleSearchColumnFields.LabStatus,
                sortable: false,
                resizable: true
              },
              {
                headerName: 'Biosample Status',
                field: 'BioSample.Status',
                suppressSizeToFit: true,
                cellRenderer: 'span',
                maxWidth: 180,
                minWidth: 80,
                cellStyle: (params) => {
                  if (params.value === 'Canceled' || params.value === 'QcFailed') {
                      return { fontWeight: 'bold' };
                  } else {
                      return null;
                  }
                },
                filterActive: false,
                colId: BiosampleSearchColumnFields.Status,
                sortable: false,
                resizable: true
              }
        ];
    }
}